import { useEffect, useRef, useState } from 'react';
import type { UserSummary } from '../lib/api';

interface Props {
  users: UserSummary[];
  value: string | null;
  onChange: (userId: string | null) => void;
  allowEmpty?: boolean;
  emptyLabel?: string;
  placeholder?: string;
  className?: string;
}

export function UserPicker({
  users,
  value,
  onChange,
  allowEmpty,
  emptyLabel = 'Unassigned',
  placeholder = 'Search…',
  className,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const selected = users.find((u) => u.id === value) ?? null;
  const filtered = query
    ? users.filter((u) =>
        (u.name + ' ' + u.email).toLowerCase().includes(query.toLowerCase()),
      )
    : users;

  return (
    <div ref={rootRef} className={`relative ${className ?? ''}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="input text-left flex items-center justify-between gap-2"
      >
        <span className={selected ? 'text-slate-900' : 'text-slate-500'}>
          {selected ? selected.name : emptyLabel}
        </span>
        <svg className="w-4 h-4 text-slate-500" viewBox="0 0 20 20" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M5.3 7.3a1 1 0 011.4 0L10 10.6l3.3-3.3a1 1 0 111.4 1.4l-4 4a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-auto">
          <div className="p-2 border-b border-slate-100 sticky top-0 bg-white">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder}
              className="w-full px-2 py-1 text-sm border border-slate-200 rounded"
            />
          </div>
          {allowEmpty && (
            <button
              type="button"
              onClick={() => {
                onChange(null);
                setOpen(false);
                setQuery('');
              }}
              className="w-full text-left px-3 py-2 text-sm text-slate-500 hover:bg-slate-50"
            >
              {emptyLabel}
            </button>
          )}
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-slate-500">No matches</div>
          ) : (
            filtered.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => {
                  onChange(u.id);
                  setOpen(false);
                  setQuery('');
                }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${u.id === value ? 'bg-brand-50 text-brand-700' : 'text-slate-900'}`}
              >
                <div>{u.name}</div>
                <div className="text-xs text-slate-500">{u.email}</div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
